import {moment} from "@/src/modules/core/lib";
import type {GetListResponse, ParamsOptions} from "@/src/modules/core/types";

import {meService} from "../../core/services/me";
import {propertiesApi} from "../api";
import {normalizeProperty} from "../lib/property-address";
import {
  EmergencyContact,
  Property,
  PropertyCitationType,
  PropertyForCreate,
  PropertyForRequest,
  PropertyForm,
  PropertyPool,
  PropertyType,
} from "../types";

class PropertiesService {
  async getProperties(options: ParamsOptions): Promise<GetListResponse<Property>> {
    const {data} = await propertiesApi.getProperties(options);
    const properties = data.data.map((row) => normalizeProperty(row));
    const meta = data.meta;

    return {data: properties, meta};
  }

  async getProperty(id: number): Promise<Property> {
    const {data} = await propertiesApi.getProperty(id);
    const row = data.data as Property & {property_citation_types?: PropertyCitationType[]};
    const {property_citation_types, citation_types, ...rest} = row;
    return normalizeProperty({
      ...rest,
      citation_types: citation_types ?? property_citation_types,
    });
  }

  async getEmergencyContacts(): Promise<EmergencyContact[]> {
    const {data} = await propertiesApi.getEmergencyContacts();
    return data.contact_telephones;
  }

  async getCitationTypes(): Promise<PropertyCitationType[]> {
    const {data} = await propertiesApi.getCitationTypes();
    return data.citation_types;
  }

  async createProperty(propertyData: PropertyForCreate): Promise<Property> {
    const {data} = await propertiesApi.createProperty(propertyData);
    return data.data;
  }

  async updateProperty({id, ...propertyData}: PropertyForRequest): Promise<Property> {
    const {data} = await propertiesApi.updateProperty(id, propertyData);
    return normalizeProperty(data.data);
  }

  async deleteProperty(id: number): Promise<void> {
    await propertiesApi.deleteProperty(id);
  }

  async getPropertyTypes(): Promise<PropertyType[]> {
    const {data} = await propertiesApi.getPropertyTypes();
    return data.property_types;
  }

  getIsScheduled(property: PropertyForm | Property): boolean {
    if (!property.valid_from) return false;
    const today = moment();
    const isScheduledInTheFuture = moment(property.valid_from).isAfter(today);

    return isScheduledInTheFuture;
  }

  getIsExpired(property: PropertyForm | Property): boolean {
    if (!property.valid_to) return false;
    const today = moment();
    const isExpired = moment(property.valid_to).isBefore(today);

    return isExpired;
  }

  getStatus(property: PropertyForm | Property): string {
    if (this.getIsScheduled(property)) return "scheduled";
    if (this.getIsExpired(property)) return "expired";
    if (property.status === 0) return "inactive";
    return "active";
  }

  private parseMessages(messages: string[]): string {
    if (messages.length === 1) return messages[0];
    if (messages.length === 2) return messages.join(" and ");
    return messages.slice(0, -1).join(", ") + " and " + messages[messages.length - 1];
  }

  getMissingConfiguration(
    property: PropertyForm | Property,
    options?: { includePrimaryContacts?: boolean },
  ): string | null {
    const includePrimaryContacts = options?.includePrimaryContacts ?? true;
    const isDispatch = Boolean(property.is_dispatch);
    const hasPrimaryContacts = property.contacts.length > 0;
    const hasPropertyManagers = property.property_managers.length > 0;
    const hasEmergencyContacts = property.contact_telephones.length > 0;

    if (isDispatch) {
      if (!includePrimaryContacts || hasPrimaryContacts) return null;
      return "Primary contacts are missing";
    }

    const isComplete = includePrimaryContacts
      ? hasPrimaryContacts && hasEmergencyContacts
      : hasEmergencyContacts;
    if (isComplete) return null;

    const messages: string[] = [];
    if (includePrimaryContacts && !hasPrimaryContacts) messages.push("Primary contacts");
    if (!hasPropertyManagers) messages.push("Property managers");
    if (!hasEmergencyContacts) messages.push("Emergency contacts");

    return `${this.parseMessages(messages)} are missing`;
  }

  async getPropertyPools(options: ParamsOptions): Promise<PropertyPool[]> {
    const {data} = await propertiesApi.getPropertyPools(options);
    return data.data;
  }

  getIsCreateEnabled(): boolean {
    return meService.getHasPermission("property store");
  }

  getIsUpdateEnabled(): boolean {
    return meService.getHasPermission("property update");
  }

  getIsDeleteEnabled(): boolean {
    return meService.getHasPermission("property delete");
  }

  getIsStatusChangeEnabled(): boolean {
    return meService.getHasPermission("property status");
  }
}

export const propertiesService = new PropertiesService();
